/**
 * Display metadata for each measurement.
 *
 * Presentation only — labels, units, input precision, and the scale a
 * comparator bar spans. **Target ranges deliberately live server-side** in
 * api/chemistry/targets.py, because the free-chlorine target is computed from
 * CYA and duplicating that here would let the two drift apart.
 *
 * `scaleMin`/`scaleMax` are the span of the comparator bar: wide enough to show
 * a bad reading in context, narrow enough that normal water isn't a dot in the
 * middle. They are not validation bounds.
 */

import type { Reading } from "@/types";

export type ParamKey =
  | "fc"
  | "cc"
  | "ph"
  | "ta"
  | "ch"
  | "cya"
  | "water_temp_f"
  | "filter_pressure_psi"
  | "tds"
  | "salt"
  | "borate";

export interface ParamMeta {
  key: ParamKey;
  label: string;
  full: string;
  unit: string;
  /** Reagent-derived accent, matching the dye you actually see in the vial. */
  color: string;
  scaleMin: number;
  scaleMax: number;
  decimals: number;
  /** Shown on the quick-check form — the ones checked most often. */
  quick: boolean;
  hint?: string;
}

export const PARAMS: Record<ParamKey, ParamMeta> = {
  fc: {
    key: "fc",
    label: "FC",
    full: "Free chlorine",
    unit: "ppm",
    color: "var(--color-reagent-dpd)",
    scaleMin: 0,
    scaleMax: 15,
    decimals: 1,
    quick: true,
    hint: "Target depends on your CYA level.",
  },
  cc: {
    key: "cc",
    label: "CC",
    full: "Combined chlorine",
    unit: "ppm",
    color: "var(--color-reagent-dpd)",
    scaleMin: 0,
    scaleMax: 3,
    decimals: 1,
    quick: false,
    hint: "Above 0.5 means something is being oxidised.",
  },
  ph: {
    key: "ph",
    label: "pH",
    full: "pH",
    unit: "",
    color: "var(--color-reagent-phenol)",
    scaleMin: 6.8,
    scaleMax: 8.4,
    decimals: 1,
    quick: true,
  },
  ta: {
    key: "ta",
    label: "TA",
    full: "Total alkalinity",
    unit: "ppm",
    color: "var(--color-reagent-bromocresol)",
    scaleMin: 0,
    scaleMax: 180,
    decimals: 0,
    quick: false,
    hint: "pH's buffer — it sets how much acid a pH change needs.",
  },
  ch: {
    key: "ch",
    label: "CH",
    full: "Calcium hardness",
    unit: "ppm",
    color: "var(--color-reagent-calcium)",
    scaleMin: 0,
    scaleMax: 700,
    decimals: 0,
    quick: false,
  },
  cya: {
    key: "cya",
    label: "CYA",
    full: "Cyanuric acid",
    unit: "ppm",
    color: "var(--color-reagent-cyanuric)",
    scaleMin: 0,
    // Headroom for a neglected pool. The Taylor CYA test reads to 100; above
    // that you dilute the sample 50/50 with distilled water and double the
    // result, which is how a 157 gets measured at all. A 120 ceiling pegged
    // the marker and hid exactly the situation worth seeing.
    scaleMax: 200,
    decimals: 0,
    quick: false,
    hint: "Sets the free-chlorine target. Tabs push it up.",
  },
  water_temp_f: {
    key: "water_temp_f",
    label: "Temp",
    full: "Water temperature",
    unit: "°F",
    color: "var(--color-reagent-phenol)",
    scaleMin: 50,
    scaleMax: 100,
    decimals: 1,
    quick: true,
  },
  filter_pressure_psi: {
    key: "filter_pressure_psi",
    label: "Filter",
    full: "Filter pressure",
    unit: "psi",
    color: "var(--color-ink-muted)",
    scaleMin: 0,
    scaleMax: 40,
    decimals: 1,
    quick: true,
    hint: "Rising above the clean baseline means backwash.",
  },
  tds: {
    key: "tds",
    label: "TDS",
    full: "Total dissolved solids",
    unit: "ppm",
    color: "var(--color-ink-muted)",
    scaleMin: 0,
    scaleMax: 4000,
    decimals: 0,
    quick: false,
    hint: "Usually only from a pool-store panel. Feeds the saturation index.",
  },
  salt: {
    key: "salt",
    label: "Salt",
    full: "Salt",
    unit: "ppm",
    color: "var(--color-ink-muted)",
    scaleMin: 0,
    scaleMax: 4000,
    decimals: 0,
    quick: false,
  },
  borate: {
    key: "borate",
    label: "Borate",
    full: "Borate",
    unit: "ppm",
    // No reagent colour: there is no borate test in the K-2006, so there is no
    // dye to borrow. Grey, with TDS and salt, is the honest signal that this
    // number came off someone else's printout.
    color: "var(--color-ink-muted)",
    scaleMin: 0,
    // Borate strips read to 80 and the band people aim at is 30-50, so this
    // spans the whole useful range. Salt's 4000 would put every real reading in
    // the leftmost pixel.
    scaleMax: 80,
    decimals: 0,
    quick: false,
    hint: "Optional — a pool with none is normal. Needs its own strip; not in the K-2006.",
  },
};

/**
 * Total chlorine above which the phenol red pH test stops being trustworthy.
 *
 * This is a **form-visibility trigger, not a chemistry decision**. The server
 * owns the guard: `api/chemistry/targets.PH_CHLORINE_INTERFERENCE_PPM` is what
 * actually decides whether a pH dose is issued, and this copy only decides
 * whether the "tested with R-0007" checkbox is worth putting on a 390px screen.
 * Getting it wrong costs a checkbox shown a little early or late; it cannot
 * change what goes in the water.
 *
 * It is duplicated rather than fetched because the log-reading screen is the
 * one you use standing at the pool on a phone, and it currently loads nothing.
 * Adding a request would mean a failed fetch could hide the checkbox — which is
 * precisely the lockout this whole feature exists to undo. The two copies are
 * pinned together by a test (`tests/chemistry/test_targets.py`) that reads this
 * file, the same "two independent transcriptions must agree" trick the FC/CYA
 * chart uses.
 */
export const PH_CHLORINE_INTERFERENCE_PPM = 10;

/**
 * Wording of the R-0007 checkbox.
 *
 * The server quotes this string back at the user inside the "no pH dose was
 * suggested" note — "tick 'pH tested with R-0007' on the reading" — so the two
 * have to say the same thing or the app is giving an instruction that names a
 * control which does not exist. Pinned to
 * `api/chemistry/targets.PH_NEUTRALISED_LABEL` by a test.
 */
export const PH_NEUTRALISED_LABEL = "pH tested with R-0007";

/**
 * The chlorine the phenol red indicator has to survive: total, not free.
 *
 * Mirrors `ph_test_chlorine` on the server. CC is normally blank on a quick
 * check, and FC alone is then a lower bound — good enough to decide whether to
 * offer a checkbox.
 */
export function phTestChlorine(fc: string | undefined, cc: string | undefined): number | null {
  const parse = (raw: string | undefined) => {
    const trimmed = (raw ?? "").trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  const free = parse(fc);
  const combined = parse(cc);
  if (free === null && combined === null) return null;
  return (free ?? 0) + (combined ?? 0);
}

export const QUICK_PARAMS: ParamKey[] = (Object.keys(PARAMS) as ParamKey[]).filter(
  (k) => PARAMS[k].quick,
);

export const FULL_PARAMS: ParamKey[] = ["fc", "cc", "ph", "ta", "ch", "cya"];

export const EXTRA_PARAMS: ParamKey[] = [
  "water_temp_f",
  "filter_pressure_psi",
  "tds",
  "salt",
  "borate",
];

/** API returns Decimals as strings to avoid float drift; parse at the edge. */
export function readValue(reading: Reading, key: ParamKey): number | null {
  const raw = reading[key];
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function formatValue(value: number | null, meta: ParamMeta): string {
  if (value === null) return "—";
  return value.toFixed(meta.decimals);
}

export const CLARITY_LABELS: Record<string, string> = {
  clear: "Clear",
  slightly_hazy: "Slightly hazy",
  hazy: "Hazy",
  cloudy: "Cloudy",
  green: "Green",
};
